/**
 * Secret code sheets — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_tabletop_iii`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const secretCodeSheetsPack: ToilePack = {
    slug: 'secret-code-sheets',
    category: 'tabletop',
    name: 'Secret code sheets',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'pigpen', label: 'Pigpen key', subject: 'a sheet titled "Pigpen Cipher" with the two tic-tac-toe grids and two X shapes holding the letters A to Z, and a row of empty boxes below for a message'},
        {key: 'morse', label: 'Morse chart', subject: 'a sheet titled "Morse Code" with the letters A to Z in two columns each beside its dots and dashes, and a ruled line at the foot for practice'},
        {key: 'decoder', label: 'Decoder sheet', subject: 'a sheet titled "Secret Message" with three rows of empty letter boxes, each with a small blank symbol box above it, and a box marked "Key" in the corner'},
    ],
    styles: [
        {key: 'spy', label: 'Spy dossier', style: 'a classified spy dossier look with typewriter-style capitals, thin rules and a faint grid', palette: 'black ink on pale manila with one red stamp-colored accent', framing: 'the page filled inside a wide margin'},
        {key: 'kids', label: 'Kids puzzle', style: "a cheerful kids' puzzle page with chunky rounded lines and playful boxes", palette: 'bright blue, orange and yellow accents on white', framing: 'centered on the page with a wide margin'},
        {key: 'parchment', label: 'Treasure parchment', style: 'an aged treasure-map parchment with inked linework and darkened edges', palette: 'sepia ink on warm tan paper', framing: 'centered on the page with an even margin'},
    ],
    directions: ['pirate theme', 'glow in dark', 'red ink'],
    directionHint: 'try: pirate theme',
    seo: {
        title: 'Secret code sheets: pigpen key, Morse chart, decoder',
        metaDescription: 'Printable secret code sheets in three styles: a pigpen cipher key, a Morse code chart and a blank decoder sheet with letter boxes.',
        h1: 'Secret code sheets: pigpen key, Morse code chart, decoder sheet',
        tagline: 'Nine sheets: a pigpen key, a Morse chart and a decoder sheet, in three styles.',
        intro: 'Secret code sheets are for writing and cracking secret messages, a classroom favourite and a treasure-hunt staple. This pack shows a pigpen cipher key, a Morse code chart with a practice line, and a blank decoder sheet with three rows of letter boxes. Each comes as a spy dossier, a kids puzzle page or a treasure-map parchment. Type a theme, such as pirate or red ink, and the sheet keeps its key and boxes.',
        useCases: [
            'a classroom lesson on codes',
            'a treasure hunt with a written clue',
            'a message a child writes to a friend',
            'a key pinned up beside a puzzle',
        ],
        faq: [
            {q: 'What is a pigpen cipher?', a: 'A cipher in which each letter is drawn as a shape taken from two small grids and two X shapes; the key sheet shows which shape is which letter.'},
            {q: 'How does the decoder sheet work?', a: 'It has three rows of empty letter boxes, each with a blank symbol box above it, and a box marked Key in the corner for the key.'},
            {q: 'Can I print the Morse chart?', a: 'Yes. It lists A to Z in two columns, each letter with its dots and dashes, and a ruled line at the foot for practice.'},
        ],
    },
}
